
import React, { useEffect, useState, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, Spinner } from 'react-bootstrap';
import SignatureCanvas from 'react-signature-canvas';
import Swal from 'sweetalert2';
import { handleImageUpload } from '../../api/uploads';

const AllergyActionPlanFormComp = ({ onSubmit, onCancel, remarks, defaultData }) => {
    const signaturePadRef = useRef(null);
    // Tracks whether the parent has actually drawn on the pad (set via
    // onEnd below, which only fires for a real pointer/touch stroke - never
    // from the programmatic fromDataURL() load in the effect below that
    // draws an existing signature onto it). Used at submit time to decide
    // whether there's a new signature to upload, rather than just checking
    // whether the pad looks non-empty - which it will, for as long as an
    // existing signature was successfully pre-loaded onto it, even though
    // nothing new was drawn.
    const signatureDirtyRef = useRef(false);
    // Nothing here previously disabled the Submit button while onSubmit's
    // promise was in flight, so a fast double-click (or a slow network
    // response) could fire the submit handler twice. Mirrors the same guard
    // used in LunchPlanWaiverFormComp.
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        is_peanuts: 0,
        is_shellfish: 0,
        shellfish_specify: '',
        is_tree_nuts: 0,
        is_fish: 0,
        fish_specify: '',
        is_milk: 0,
        is_food_additives: 0,
        food_additives_specify: '',
        all_dairy_products: 0,
        insect_stings_bites: 0,
        insect_stings_bites_specify: '',
        insect_stings_bites_medication: '',
        is_other: 0,
        other_specify: '',
        symptoms: '',
        emergency_treatment_procedures: '',
        require_epi_pen: 0,
        location_of_epi_pen: '',
        parent_signature: '',
        parent_signature_date: '',
        is_allergy_form: 0,
        is_preference_form: 0
    });

    useEffect(() => {
        if (defaultData) {
            setFormData(prev => ({
                ...prev,
                ...defaultData,
                is_allergy_form: defaultData.is_allergy_form ?? 0,
                is_preference_form: defaultData.is_preference_form ?? 0
            }));
        }
        // eslint-disable-next-line
    }, [defaultData]);

    // Draws an existing signature URL directly onto the pad on edit, so it
    // shows up already signed instead of a blank pad next to a value the
    // rest of the form silently already knew about. Fetches it as a blob
    // first and hands the pad a same-origin blob: URL rather than a plain
    // cross-origin one - drawing a cross-origin image straight onto the
    // canvas permanently taints it, and every later toDataURL() call (i.e.
    // re-saving this pad) then throws a SecurityError instead of producing
    // an image, even for a resubmit that never touched the signature.
    // Deliberately no fallback to a direct (non-blob) draw if this fetch
    // fails - confirmed elsewhere in this app that doing so, while it does
    // make the pad preview correctly, still leaves toDataURL() throwing on
    // a later submit and breaks it entirely. A signature that doesn't
    // preview here is a much smaller problem than a submission that
    // hard-fails.
    useEffect(() => {
        const url = defaultData?.parent_signature;
        if (!url) return;

        let cancelled = false;
        let attempts = 0;
        let timeoutId;

        const tryDraw = () => {
            attempts += 1;
            const pad = signaturePadRef.current;
            if (!pad) {
                if (attempts < 10) timeoutId = setTimeout(tryDraw, 150);
                return;
            }
            if (!pad.isEmpty()) return;

            fetch(url)
                .then((res) => {
                    if (!res.ok) throw new Error(`Signature fetch failed: ${res.status}`);
                    return res.blob();
                })
                .then((blob) => {
                    if (cancelled || !signaturePadRef.current || !signaturePadRef.current.isEmpty()) return;
                    const objectUrl = URL.createObjectURL(blob);
                    signaturePadRef.current.fromDataURL(objectUrl);
                    setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
                })
                .catch((e) => {
                    console.log('Could not load existing signature - leaving the pad blank:', e);
                });
        };

        timeoutId = setTimeout(tryDraw, 100);
        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
        };
    }, [defaultData?.parent_signature]);

    const handleCheckboxChange = (field) => {
        setFormData(prev => ({
            ...prev,
            [field]: prev[field] === 1 ? 0 : 1
        }));
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleClearSignature = () => {
        if (signaturePadRef.current) {
            signaturePadRef.current.clear();
        }
        signatureDirtyRef.current = false;
        // Previously left formData.parent_signature untouched, so "Clear
        // Signature" only ever cleared the pad visually - the old URL was
        // still what got submitted (since the now-empty pad meant nothing
        // new to upload), making Clear a no-op from the saved record's
        // point of view.
        setFormData(prev => ({ ...prev, parent_signature: '' }));
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        if (isSubmitting) {
            return;
        }

        if (!formData.symptoms || !formData.emergency_treatment_procedures) {
            Swal.fire({
                icon: 'warning',
                title: 'Required Fields',
                text: 'Please fill in symptoms and emergency treatment procedures.'
            });
            return;
        }
        const hasSignaturePadContent = signaturePadRef.current && !signaturePadRef.current.isEmpty();
        if (!hasSignaturePadContent && !formData.parent_signature) {
            Swal.fire({
                icon: 'warning',
                title: 'Signature Required',
                text: 'Please provide parent signature.'
            });
            return;
        }
        if (!formData.parent_signature_date) {
            Swal.fire({
                icon: 'warning',
                title: 'Date Required',
                text: 'Please provide signature date.'
            });
            return;
        }

        setIsSubmitting(true);

        let submitData = { ...formData };
        // Only re-upload if the pad has something the parent actually drew
        // (signatureDirtyRef) - not just "the pad looks non-empty", which
        // it will for as long as an existing signature is loaded onto it
        // (see the effect above), even when nothing new was drawn.
        if (signatureDirtyRef.current && hasSignaturePadContent) {
            try {
                const signatureDataURL = signaturePadRef.current.toDataURL('image/png');
                const signatureBlob = await fetch(signatureDataURL).then(res => res.blob());
                const signatureFile = new File([signatureBlob], `parent_signature_${Date.now()}.png`, { type: 'image/png' });
                const signatureUrl = await handleImageUpload(signatureFile, 'signatures/', 'image/png');
                submitData.parent_signature = signatureUrl;
            } catch (error) {
                // A tainted canvas (or any other upload failure) shouldn't
                // hard-fail the whole submission - fall back to whatever
                // signature value was already on file.
                console.error('Could not upload signature - keeping the existing value:', error);
            }
        }

        // Show loader during submission
        Swal.fire({
            title: 'Processing...',
            html: 'Saving allergy action plan...',
            allowOutsideClick: false,
            allowEscapeKey: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });
        
        try {
            await onSubmit(submitData);
            Swal.close();
            signatureDirtyRef.current = false;
        } catch (error) {
            Swal.close();
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.message || 'Failed to save allergy action plan. Please try again.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Container>
            <Row>
                <Col sm="12">
                    <Card>
                        {remarks &&
                            <Card.Header className="d-flex justify-content-between align-items-center">
                                <div className="header-title">
                                    <h4 className="card-title">
                                        {remarks}
                                    </h4>
                                </div>
                            </Card.Header>
                        }
                        <Card.Body>
                            <Form onSubmit={handleFormSubmit}>
                                <div className="mb-4 p-3 d-flex gap-4" style={{ background: '#f8f9fa', border: '1px solid #eef0f3', borderRadius: '10px' }}>
                                    <Form.Check
                                        type="checkbox"
                                        id="is_allergy_form"
                                        label="Allergies"
                                        checked={!!formData.is_allergy_form}
                                        onChange={() => handleInputChange('is_allergy_form', formData.is_allergy_form ? 0 : 1)}
                                        className="mb-0 custom-checkbox"
                                    />
                                    <Form.Check
                                        type="checkbox"
                                        id="is_preference_form"
                                        label="Preferences"
                                        checked={!!formData.is_preference_form}
                                        onChange={() => handleInputChange('is_preference_form', formData.is_preference_form ? 0 : 1)}
                                        className="mb-0 custom-checkbox"
                                    />
                                </div>

                                <div className="mb-5 pb-4 border-bottom">
                                    <div className="d-flex align-items-center mb-4">
                                        <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', backgroundColor: '#6f42c1' }}>
                                            <i className="material-symbols-outlined">restaurant</i>
                                        </div>
                                        <h5 className="mb-0 fw-bold text-dark">Food Allergies</h5>
                                    </div>
                                    <Row className="mb-3">
                                    <Col md="6">
                                        <Form.Check
                                            type="checkbox"
                                            id="is_peanuts"
                                            label="Peanuts"
                                            checked={formData.is_peanuts === 1}
                                            onChange={() => handleCheckboxChange('is_peanuts')}
                                            className="mb-3 custom-checkbox"
                                        />
                                        <Form.Check
                                            type="checkbox"
                                            id="is_shellfish"
                                            label="Shellfish"
                                            checked={formData.is_shellfish === 1}
                                            onChange={() => handleCheckboxChange('is_shellfish')}
                                            className="mb-3 custom-checkbox"
                                            style={{ marginLeft: '20px' }}
                                        />
                                        {formData.is_shellfish === 1 && (
                                            <Form.Group className="mb-2 ms-4">
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Specify shellfish type"
                                                    value={formData.shellfish_specify}
                                                    onChange={(e) => handleInputChange('shellfish_specify', e.target.value)}
                                                />
                                            </Form.Group>
                                        )}
                                        <Form.Check
                                            type="checkbox"
                                            id="is_tree_nuts"
                                            label="Tree Nuts"
                                            checked={formData.is_tree_nuts === 1}
                                            onChange={() => handleCheckboxChange('is_tree_nuts')}
                                            className="mb-3 custom-checkbox"
                                            style={{ marginLeft: '20px' }}
                                        />
                                        <Form.Check
                                            type="checkbox"
                                            id="is_fish"
                                            label="Fish"
                                            checked={formData.is_fish === 1}
                                            onChange={() => handleCheckboxChange('is_fish')}
                                            className="mb-3 custom-checkbox"
                                            style={{ marginLeft: '20px' }}
                                        />
                                        {formData.is_fish === 1 && (
                                            <Form.Group className="mb-2 ms-4">
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Specify fish type"
                                                    value={formData.fish_specify}
                                                    onChange={(e) => handleInputChange('fish_specify', e.target.value)}
                                                />
                                            </Form.Group>
                                        )}
                                    </Col>
                                    <Col md="6">
                                        <Form.Check
                                            type="checkbox"
                                            id="is_milk"
                                            label="Milk"
                                            checked={formData.is_milk === 1}
                                            onChange={() => handleCheckboxChange('is_milk')}
                                            className="mb-3 custom-checkbox"
                                            style={{ marginLeft: '20px' }}
                                        />
                                        <Form.Check
                                            type="checkbox"
                                            id="is_food_additives"
                                            label="Food Additives"
                                            checked={formData.is_food_additives === 1}
                                            onChange={() => handleCheckboxChange('is_food_additives')}
                                            className="mb-3 custom-checkbox"
                                            style={{ marginLeft: '20px' }}
                                        />
                                        {formData.is_food_additives === 1 && (
                                            <Form.Group className="mb-2 ms-4">
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Specify food additives"
                                                    value={formData.food_additives_specify}
                                                    onChange={(e) => handleInputChange('food_additives_specify', e.target.value)}
                                                />
                                            </Form.Group>
                                        )}
                                        <Form.Check
                                            type="checkbox"
                                            id="all_dairy_products"
                                            label="All Dairy Products"
                                            checked={formData.all_dairy_products === 1}
                                            onChange={() => handleCheckboxChange('all_dairy_products')}
                                            className="mb-3 custom-checkbox"
                                            style={{ marginLeft: '20px' }}
                                        />
                                        <Form.Check
                                            type="checkbox"
                                            id="is_other"
                                            label="Other"
                                            checked={formData.is_other === 1}
                                            onChange={() => handleCheckboxChange('is_other')}
                                            className="mb-3 custom-checkbox"
                                            style={{ marginLeft: '20px' }}
                                        />
                                        {formData.is_other === 1 && (
                                            <Form.Group className="mb-2 ms-4">
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Specify other allergies"
                                                    value={formData.other_specify}
                                                    onChange={(e) => handleInputChange('other_specify', e.target.value)}
                                                />
                                            </Form.Group>
                                        )}
                                    </Col>
                                </Row>
                                </div>

                                <div className="mb-5 pb-4 border-bottom">
                                    <div className="d-flex align-items-center mb-4">
                                        <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', backgroundColor: '#6f42c1' }}>
                                            <i className="material-symbols-outlined">pest_control</i>
                                        </div>
                                        <h5 className="mb-0 fw-bold text-dark">Insect Stings/Bites</h5>
                                    </div>
                                    <Row className="mb-3">
                                    <Col md="12">
                                        <Form.Check
                                            type="checkbox"
                                            id="insect_stings_bites"
                                            label="Has Insect Sting/Bite Allergy"
                                            checked={formData.insect_stings_bites === 1}
                                            onChange={() => handleCheckboxChange('insect_stings_bites')}
                                            className="mb-3 custom-checkbox"
                                        />
                                        {formData.insect_stings_bites === 1 && (
                                            <>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Specify Insect Type</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="e.g., Bee sting, Wasp, etc."
                                                        value={formData.insect_stings_bites_specify}
                                                        onChange={(e) => handleInputChange('insect_stings_bites_specify', e.target.value)}
                                                    />
                                                </Form.Group>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Medication for Insect Sting/Bite</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={2}
                                                        placeholder="e.g., Apply ice, Benadryl syrup"
                                                        value={formData.insect_stings_bites_medication}
                                                        onChange={(e) => handleInputChange('insect_stings_bites_medication', e.target.value)}
                                                    />
                                                </Form.Group>
                                            </>
                                        )}
                                    </Col>
                                </Row>
                                </div>

                                <div className="mb-5 pb-4 border-bottom">
                                    <div className="d-flex align-items-center mb-4">
                                        <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', backgroundColor: '#dc3545' }}>
                                            <i className="material-symbols-outlined">emergency</i>
                                        </div>
                                        <h5 className="mb-0 fw-bold text-dark">Symptoms & Emergency Treatment</h5>
                                    </div>
                                    <Row className="mb-3">
                                    <Col md="12">
                                        <Form.Group className="mb-3">
                                            <Form.Label>Symptoms <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={3}
                                                placeholder="Describe allergic reaction symptoms"
                                                value={formData.symptoms}
                                                onChange={(e) => handleInputChange('symptoms', e.target.value)}
                                                required
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Emergency Treatment Procedures <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={3}
                                                placeholder="Describe emergency treatment steps"
                                                value={formData.emergency_treatment_procedures}
                                                onChange={(e) => handleInputChange('emergency_treatment_procedures', e.target.value)}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                </div>

                                <div className="mb-5 pb-4 border-bottom">
                                    <div className="d-flex align-items-center mb-4">
                                        <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', backgroundColor: '#6f42c1' }}>
                                            <i className="material-symbols-outlined">medication</i>
                                        </div>
                                        <h5 className="mb-0 fw-bold text-dark">EpiPen Information</h5>
                                    </div>
                                    <Row className="mb-3">
                                    <Col md="12">
                                        <Form.Check
                                            type="checkbox"
                                            id="require_epi_pen"
                                            label="Requires EpiPen"
                                            checked={formData.require_epi_pen === 1}
                                            onChange={() => handleCheckboxChange('require_epi_pen')}
                                            className="mb-3 custom-checkbox"
                                        />
                                        {formData.require_epi_pen === 1 && (
                                            <Form.Group className="mb-3">
                                                <Form.Label>Location of EpiPen</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="e.g., In the classroom first-aid kit"
                                                    value={formData.location_of_epi_pen}
                                                    onChange={(e) => handleInputChange('location_of_epi_pen', e.target.value)}
                                                />
                                            </Form.Group>
                                        )}
                                    </Col>
                                </Row>
                                </div>

                                {/* Consent & Acknowledgement */}
                                <div className="mb-4 p-3 d-flex align-items-start gap-2" style={{ background: '#f0f9f1', border: '1px solid #a8dab5', borderRadius: '10px' }}>
                                    <i className="material-symbols-outlined text-success flex-shrink-0" style={{ fontSize: '20px' }}>check_circle</i>
                                    <div style={{ fontSize: '14px' }}>
                                        <p className="mb-2">By signing this form, I acknowledge and agree to the following:</p>
                                        <ul className="mb-0 ps-3">
                                            <li className="mb-1">I give Harmony Arts Daycare and Education Centre staff permission to administer my child's epi-pen.</li>
                                            <li className="mb-1">I give permission for staff to call 911 immediately if my child experiences an anaphylactic reaction.</li>
                                            <li className="mb-1">Staff will follow the procedures in this action plan whether or not I or my emergency contact can be reached.</li>
                                            <li className="mb-0">I am responsible for informing the daycare of any required updates to this plan.</li>
                                        </ul>
                                    </div>
                                </div>

                                {/* Signature Section */}
                                <div className="mb-2">
                                    <div className="d-flex align-items-center mb-4">
                                        <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', backgroundColor: '#6f42c1' }}>
                                            <i className="material-symbols-outlined">draw</i>
                                        </div>
                                        <h5 className="mb-0 fw-bold text-dark">Parent Signature</h5>
                                    </div>
                                    <Row className="mb-3">
                                        <Col md="12">
                                            <Form.Group className="mb-3">
                                                <Form.Label>Signature <span className="text-danger">*</span></Form.Label>
                                                <div className="border rounded p-2 bg-white" style={{ width: '100%', height: '200px', borderRadius: '8px' }}>
                                                    <SignatureCanvas
                                                        ref={signaturePadRef}
                                                        clearOnResize={false}
                                                        onEnd={() => { signatureDirtyRef.current = true; }}
                                                        canvasProps={{
                                                            className: 'signature-canvas',
                                                            style: { width: '100%', height: '100%' }
                                                        }}
                                                    />
                                                </div>
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    className="mt-2"
                                                    onClick={handleClearSignature}
                                                    type="button"
                                                >
                                                    <i className="material-symbols-outlined me-1" style={{ fontSize: '16px', verticalAlign: 'middle' }}>ink_eraser</i>
                                                    Clear Signature
                                                </Button>
                                            </Form.Group>
                                        </Col>
                                        <Col md="6">
                                            <Form.Group className="mb-3">
                                                <Form.Label>Signature Date <span className="text-danger">*</span></Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    value={formData.parent_signature_date}
                                                    onChange={(e) => handleInputChange('parent_signature_date', e.target.value)}
                                                    max={new Date().toISOString().split('T')[0]}
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </div>

                                <div className="d-flex justify-content-end gap-2 pt-3 mt-2" style={{ borderTop: '1px solid #eef0f3' }}>
                                    {onCancel && (
                                        <Button
                                            variant="outline-secondary"
                                            onClick={onCancel}
                                            disabled={isSubmitting}
                                        >
                                            <i className="material-symbols-outlined me-1" style={{ verticalAlign: 'middle' }}>close</i>
                                            Cancel
                                        </Button>
                                    )}
                                    <Button
                                        variant="primary"
                                        type="submit"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Submitting...
                                            </>
                                        ) : (
                                            <>
                                                <i className="material-symbols-outlined me-1" style={{ verticalAlign: 'middle' }}>check_circle</i>
                                                Submit
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default AllergyActionPlanFormComp;
